/** Send changes through the native setter so React's controlled fields receive them. */
export function setPageField(
  field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  value: string,
) {
  const prototype =
    field instanceof HTMLInputElement
      ? HTMLInputElement.prototype
      : field instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLSelectElement.prototype;
  Object.getOwnPropertyDescriptor(prototype, 'value')!.set!.call(field, value);
  field.dispatchEvent(new Event('input', { bubbles: true }));
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * What a press and its release are matched by: the control a ray lands in, not the span inside
 * it, so a hand's jitter between the two still makes a click (client-input:quality#9).
 */
const CONTROL = 'button, a, label, input, select, textarea, summary, [role="button"]';

/** DOM remains the single source of page state; XR only supplies pointing and text entry. */
export function createFloatingPageInput(root: HTMLElement, dirty: () => void) {
  let popup: HTMLElement | null = null;
  let hovered: Element | null = null;
  let down: Element | null = null;
  const close = () => {
    popup?.remove();
    popup = null;
    dirty();
  };
  const scroll = (delta: number) => {
    let target = hovered;
    while (target && target !== root) {
      if (
        target instanceof HTMLElement &&
        target.scrollHeight > target.clientHeight + 1 &&
        /auto|scroll/.test(getComputedStyle(target).overflowY)
      ) {
        target.scrollTop += delta;
        dirty();
        return;
      }
      target = target.parentElement;
    }
    const card = root.querySelector<HTMLElement>('.menu-card');
    if (card) card.scrollTop += delta;
    dirty();
  };
  const tools = document.createElement('div');
  tools.className = 'xr-page-tools';
  const button = (parent: HTMLElement, label: string, run: () => void) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.onclick = () => {
      run();
      dirty();
    };
    parent.append(b);
    return b;
  };
  button(tools, 'Scroll up', () => scroll(-240));
  button(tools, 'Scroll down', () => scroll(240));
  root.append(tools);
  const open = () => {
    close();
    popup = document.createElement('div');
    popup.className = 'xr-input-panel';
    root.append(popup);
    return popup;
  };
  const edit = (field: HTMLInputElement | HTMLTextAreaElement) => {
    const panel = open();
    let value = field.value,
      shift = false;
    const title = document.createElement('div');
    title.textContent =
      field.getAttribute('aria-label') || field.labels?.[0]?.textContent || 'Enter value';
    panel.append(title);
    const output = document.createElement('output');
    panel.append(output);
    const refresh = () => {
      output.textContent = field.type === 'password' ? '•'.repeat(value.length) : value || ' ';
    };
    refresh();
    for (const row of ['1234567890', 'qwertyuiopå', 'asdfghjklöä', 'zxcvbnm', '@.-_+/:,!?=()#']) {
      const line = document.createElement('div');
      panel.append(line);
      for (const key of row)
        button(line, key, () => {
          value += shift ? key.toUpperCase() : key;
          refresh();
        });
    }
    const shiftButton = button(panel, 'Shift', () => {
      shift = !shift;
      shiftButton.textContent = shift ? 'SHIFT ON' : 'Shift';
    });
    button(panel, 'Space', () => {
      value += ' ';
      refresh();
    });
    button(panel, 'Backspace', () => {
      value = value.slice(0, -1);
      refresh();
    });
    button(panel, 'Clear', () => {
      value = '';
      refresh();
    });
    button(panel, 'Apply', () => {
      if (field.isConnected) setPageField(field, value);
      close();
    });
    button(panel, 'Cancel', close);
  };
  const choose = (field: HTMLSelectElement) => {
    const panel = open();
    for (const option of field.options) {
      const b = button(panel, option.text, () => {
        if (field.isConnected) setPageField(field, option.value);
        close();
      });
      b.disabled = option.disabled;
    }
    button(panel, 'Cancel', close);
  };
  const range = (field: HTMLInputElement, x: number) => {
    const rect = field.getBoundingClientRect(),
      min = Number(field.min || 0),
      max = Number(field.max || 100);
    const step = field.step === 'any' ? 0 : Number(field.step || 1);
    let value = min + Math.max(0, Math.min(1, (x - rect.left) / rect.width)) * (max - min);
    if (step) value = min + Math.round((value - min) / step) * step;
    setPageField(field, String(Math.max(min, Math.min(max, value))));
    dirty();
  };
  return {
    scroll,
    close,
    point(kind: 'down' | 'move' | 'up', x: number, y: number) {
      const target = document.elementsFromPoint(x, y).find((e) => root.contains(e) && e !== root);
      const control = target ? (target.closest(CONTROL) ?? target) : null;
      if (kind === 'down') down = control;
      // What the press began on. A release ends it wherever it lands, a disabled field included:
      // left set, a sweep of the laser later dragged the next slider it crossed (client-input:bugs#4).
      const pressed = down;
      if (kind === 'up') down = null;
      if (!target) return;
      hovered = target;
      const field = target.closest('input, textarea, select');
      if (field?.matches(':disabled') || (field && 'readOnly' in field && field.readOnly)) return;
      if (field instanceof HTMLInputElement && field.type === 'range') {
        // Only the slider the press began on follows the ray, not one a drag passes over.
        if (pressed === field) range(field, x);
      } else if (kind === 'up' && pressed !== null && pressed === control) {
        if (field instanceof HTMLSelectElement) choose(field);
        else if (
          field instanceof HTMLTextAreaElement ||
          (field instanceof HTMLInputElement &&
            !['checkbox', 'radio', 'button', 'submit', 'file'].includes(field.type))
        )
          edit(field);
        else {
          // DOM click performs native checkbox/label activation and React/SVG click handlers.
          target.dispatchEvent(
            new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              clientX: x,
              clientY: y,
              view: window,
            }),
          );
        }
        dirty();
      }
    },
    dispose() {
      close();
      tools.remove();
    },
  };
}
